import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
    MapPin, 
    Loader2, 
    ArrowLeft,
    Plus,
    Star,
    AlertTriangle,
    ShieldCheck,
    Shield,
    Clock,
    CheckCircle,
    Check,
    FileText,
    TrendingUp,
    Users,
    DollarSign,
    MessageSquare,
    RefreshCw,
    Scale,
    Handshake,
    ChevronRight,
    Calendar,
    CreditCard,
    Edit,
    Mail,
    ListChecks,
    XCircle,
    Camera,
    Banknote,
    UserCheck,
    Clipboard,
    FileSignature,
    ThumbsUp,
    ThumbsDown,
    Award,
    Zap,
    ClipboardCheck,
    HardHat,
    Wrench
} from 'lucide-react';
import { formatDate, cn, formatTimeAgo } from '../lib/utils';
import { toast } from 'sonner';
import api, { reviewAPI } from '../lib/api';

/**
 * CONTRACTOR EXPERIENCE SCORE DISPLAY
 * Shows the aggregated contractor experience score as a simple X/5 rating.
 * 
 * LEGAL COMPLIANCE: This is NOT a risk score.
 * This displays an average of contractor-submitted job experience ratings.
 * Score appears when at least 1 review exists (review_count >= 1).
 */
const ExperienceScoreDisplay = ({ score, reviewCount, size = 'default' }) => {
    // Convert 100-point score to 5-point scale
    const score5Point = score !== null && score !== undefined ? (score / 20).toFixed(1) : null;
    
    const sizeClasses = {
        small: 'text-3xl',
        default: 'text-5xl',
        large: 'text-6xl'
    };
    
    // Show score when at least 1 review exists (not "not enough" - any review is enough)
    const hasScore = score !== null && score !== undefined && reviewCount >= 1;
    
    return (
        <div className="text-center" data-testid="experience-score-display">
            {hasScore ? (
                <>
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                        Contractor-Reported Experience
                    </p>
                    <div className="flex items-baseline justify-center gap-2">
                        <span className={cn(
                            "font-bold text-foreground",
                            sizeClasses[size]
                        )}>
                            {score5Point}
                        </span>
                        <span className="text-2xl text-muted-foreground">/5</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                        Based on {reviewCount} contractor review{reviewCount !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-muted-foreground/70 mt-1 max-w-xs mx-auto">
                        Based on subjective contractor opinions. Not a factual assessment of any person or property.
                    </p>
                </>
            ) : (
                <div className="py-4">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                        Contractor-Reported Experience
                    </p>
                    <span className={cn(
                        "font-bold text-muted-foreground",
                        sizeClasses[size]
                    )}>
                        —
                    </span>
                    <p className="text-sm text-muted-foreground mt-2">
                        No contractor reviews yet
                    </p>
                </div>
            )}
        </div>
    );
};

// Keep ScoreGauge for backward compatibility but update terminology
/**
 * SCORE GAUGE COMPONENT (DEPRECATED TERMINOLOGY)
 * Visual representation of contractor experience score
 * This is NOT a risk assessment - it's an aggregation of contractor ratings.
 */
const ScoreGauge = ({ score, size = 200 }) => {
    const radius = size / 2 - 20;
    const strokeWidth = 16;
    const circumference = Math.PI * radius; // Semi-circle
    
    // Neutral color scheme based on score
    const getScoreColor = (s) => {
        if (s === null || s === undefined) return '#6b7280'; // gray
        // Use a single primary color gradient instead of risk-colored zones
        return '#6366f1'; // primary/indigo color
    };
    
    // Get text color class
    const getTextColorClass = (s) => {
        if (s === null || s === undefined) return 'text-muted-foreground';
        return 'text-foreground';
    };
    
    // Calculate stroke offset (inverted for semi-circle)
    const normalizedScore = score !== null && score !== undefined ? Math.min(100, Math.max(0, score)) : 0;
    const strokeDashoffset = circumference - (normalizedScore / 100) * circumference;
    
    const color = getScoreColor(score);
    const textColorClass = getTextColorClass(score);
    
    // Convert to 5-point scale for display
    const score5Point = score !== null && score !== undefined ? (score / 20).toFixed(1) : null;
    
    return (
        <div className="relative" style={{ width: size, height: size / 2 + 30 }}>
            <svg 
                width={size} 
                height={size / 2 + 20}
                className="transform -rotate-0"
            >
                {/* Background arc (gray) */}
                <path
                    d={`M ${strokeWidth} ${size/2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth} ${size/2}`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    className="text-muted/30"
                    strokeLinecap="round"
                />
                
                {/* Score arc - uses primary color, not risk colors */}
                <path
                    d={`M ${strokeWidth} ${size/2} A ${radius} ${radius} 0 0 1 ${size - strokeWidth} ${size/2}`}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="transition-all duration-700 ease-out"
                />
            </svg>
            
            {/* Score number in center - shows X/5 format */}
            <div 
                className="absolute inset-0 flex flex-col items-center justify-end pb-2"
                style={{ height: size / 2 + 20 }}
            >
                <div className="flex items-baseline gap-1">
                    <span className={cn(
                        "text-4xl font-bold transition-colors duration-300",
                        textColorClass
                    )}>
                        {score5Point !== null ? score5Point : '—'}
                    </span>
                    {score5Point !== null && (
                        <span className="text-xl text-muted-foreground">/5</span>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * DATA AVAILABILITY BADGE
 * Shows whether summary data is available (NOT a risk badge)
 * Summary available when at least 1 review exists (review_count >= 1)
 */
const DataAvailabilityBadge = ({ reviewCount, size = 'default' }) => {
    // Any review means contractor data exists
    const hasData = reviewCount >= 1;
    
    const sizeClasses = {
        small: 'px-3 py-1 text-xs',
        default: 'px-5 py-2 text-sm',
        large: 'px-6 py-2.5 text-base'
    };
    
    if (hasData) {
        return (
            <span className={cn(
                "inline-flex items-center font-semibold rounded-full bg-primary/20 text-primary border border-primary/30",
                sizeClasses[size]
            )} data-testid="data-availability-badge">
                Contractor Data Available
            </span>
        );
    }
    
    return (
        <span className={cn(
            "inline-flex items-center font-semibold rounded-full bg-muted/50 text-muted-foreground border border-border",
            sizeClasses[size]
        )} data-testid="data-availability-badge">
            No Contractor Data Yet
        </span>
    );
};

/**
 * CONTRACTOR SENTIMENT INDICATOR
 * Shows percentage of contractors willing to work with client again
 */
const ContractorSentiment = ({ percentage, totalReviews }) => {
    const getColor = () => {
        if (percentage === null || percentage === undefined) return 'text-muted-foreground';
        if (percentage >= 80) return 'text-emerald-500';
        if (percentage >= 60) return 'text-green-500';
        if (percentage >= 40) return 'text-yellow-500';
        if (percentage >= 20) return 'text-orange-500';
        return 'text-red-500';
    };
    
    const getBgColor = () => {
        if (percentage === null || percentage === undefined) return 'bg-muted/30';
        if (percentage >= 80) return 'bg-emerald-500/10';
        if (percentage >= 60) return 'bg-green-500/10';
        if (percentage >= 40) return 'bg-yellow-500/10';
        if (percentage >= 20) return 'bg-orange-500/10';
        return 'bg-red-500/10';
    };
    
    const Icon = percentage !== null && percentage >= 50 ? ThumbsUp : ThumbsDown;
    
    return (
        <div className={cn(
            "flex items-center justify-center gap-3 py-3 px-4 rounded-xl",
            getBgColor()
        )} data-testid="contractor-sentiment">
            <Icon className={cn("h-5 w-5", getColor())} />
            <div className="text-center">
                <span className={cn("text-2xl font-bold", getColor())}>
                    {percentage !== null && percentage !== undefined ? `${Math.round(percentage)}%` : '—'}
                </span>
                <p className="text-xs text-muted-foreground mt-0.5">
                    would work with this client again
                </p>
            </div>
        </div>
    );
};

// Score bar component with neutral labels
const ScoreBar = ({ label, score, scoreLabel, icon: Icon }) => {
    const getScoreColor = (s) => {
        if (s === null || s === undefined) return 'bg-muted';
        if (s >= 80) return 'bg-emerald-500';
        if (s >= 65) return 'bg-green-500';
        if (s >= 50) return 'bg-amber-500';
        if (s >= 35) return 'bg-orange-500';
        return 'bg-red-500';
    };

    const getLabelColor = (l) => {
        if (!l || l === 'N/A') return 'text-muted-foreground';
        if (l === 'Excellent') return 'text-emerald-500';
        if (l === 'Good') return 'text-green-500';
        if (l === 'Fair') return 'text-amber-500';
        if (l === 'Below Average') return 'text-orange-500';
        return 'text-red-500';
    };

    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                    <span className="text-sm font-medium text-foreground">{label}</span>
                </div>
                <span className={cn("text-sm font-semibold", getLabelColor(scoreLabel))}>
                    {scoreLabel || 'N/A'}
                </span>
            </div>
            <Progress 
                value={score !== null && score !== undefined ? score : 0} 
                className={cn("h-2", getScoreColor(score))}
            />
        </div>
    );
};

// Quick stat card
const QuickStat = ({ icon: Icon, value, label, color = "primary" }) => {
    const colorClasses = {
        primary: 'bg-primary/10 text-primary',
        emerald: 'bg-emerald-500/10 text-emerald-500',
        amber: 'bg-amber-500/10 text-amber-500',
        blue: 'bg-blue-500/10 text-blue-500',
    };

    return (
        <div className="flex flex-col items-center p-3 rounded-lg bg-card border border-border">
            <div className={cn("p-2 rounded-full mb-2", colorClasses[color])}>
                <Icon className="h-4 w-4" />
            </div>
            <span className="text-lg font-bold text-foreground">{value}</span>
            <span className="text-xs text-muted-foreground text-center">{label}</span>
        </div>
    );
};

// Verification tier badge
const TierBadge = ({ tier }) => {
    const tiers = {
        basic: { label: 'Basic', color: 'bg-gray-500/20 text-gray-400', icon: null },
        verified: { label: 'Verified', color: 'bg-blue-500/20 text-blue-400', icon: ShieldCheck },
        trusted: { label: 'Trusted', color: 'bg-amber-500/20 text-amber-400', icon: Star },
    };
    
    const t = tiers[tier] || tiers.basic;
    const Icon = t.icon;
    
    return (
        <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full", t.color)}>
            {Icon && <Icon className="h-3 w-3" />}
            {t.label}
        </span>
    );
};

/**
 * Recommended Protections Component
 * Clean checklist-style display with check icons
 */
const ProtectionRecommendations = ({ protections }) => {
    if (!protections || !protections.show_section) return null;
    
    // Priority check colors
    const checkColors = {
        critical: 'text-red-500',
        warning: 'text-amber-500',
        important: 'text-orange-500',
        recommended: 'text-blue-500',
        advisory: 'text-muted-foreground',
        standard: 'text-emerald-500',
    };
    
    // Priority text colors
    const textColors = {
        critical: 'text-red-400',
        warning: 'text-amber-400',
        important: 'text-orange-400',
        recommended: 'text-foreground',
        advisory: 'text-muted-foreground',
        standard: 'text-foreground',
    };
    
    // Header style based on risk tier
    const headerConfig = {
        low: { bg: 'bg-emerald-500/5 border-emerald-500/20', icon: CheckCircle, iconColor: 'text-emerald-500' },
        moderate: { bg: 'bg-amber-500/5 border-amber-500/20', icon: AlertTriangle, iconColor: 'text-amber-500' },
        high: { bg: 'bg-red-500/5 border-red-500/20', icon: Shield, iconColor: 'text-red-500' },
        insufficient_data: { bg: 'bg-muted/30 border-border', icon: FileText, iconColor: 'text-muted-foreground' },
    };
    
    const config = headerConfig[protections.risk_tier] || headerConfig.insufficient_data;
    const HeaderIcon = config.icon;
    
    return (
        <Card className={cn("bg-card border-2", config.bg)} data-testid="protection-recommendations">
            <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                    <HeaderIcon className={cn("h-5 w-5", config.iconColor)} />
                    <h3 className="font-bold text-foreground">Recommended Protections</h3>
                </div>
                
                {/* Checklist - Clean simple style */}
                <div className="space-y-2 mt-3">
                    {protections.recommendations.map((rec) => (
                        <div 
                            key={rec.id}
                            className="flex items-start gap-3 py-1.5"
                        >
                            <Check className={cn(
                                "h-5 w-5 mt-0.5 flex-shrink-0",
                                checkColors[rec.priority] || checkColors.standard
                            )} />
                            <span className={cn(
                                "text-sm font-medium",
                                textColors[rec.priority] || textColors.standard
                            )}>
                                {rec.text}
                            </span>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
};

/**
 * Positive Client Indicators Component
 * Shows badges for clients with consistently good reviews
 */
const PositiveIndicators = ({ indicators }) => {
    if (!indicators || indicators.length === 0) return null;
    
    const iconMap = {
        'dollar-sign': DollarSign,
        'thumbs-up': ThumbsUp,
        'clipboard-check': ClipboardCheck,
        'zap': Zap,
        'award': Award
    };
    
    return (
        <Card className="bg-emerald-500/5 border-emerald-500/20" data-testid="positive-indicators">
            <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <Award className="h-5 w-5 text-emerald-500" />
                    <h3 className="font-bold text-foreground">Client Recognition</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                    {indicators.map((indicator) => {
                        const Icon = iconMap[indicator.icon] || CheckCircle;
                        return (
                            <div
                                key={indicator.id}
                                className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30"
                                title={indicator.description}
                            >
                                <Icon className="h-4 w-4 text-emerald-500" />
                                <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                                    {indicator.name}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};

/**
 * Work History Component
 * Shows anonymous trade summary of contractors who worked at this property
 */
const WorkHistory = ({ workHistory }) => {
    if (!workHistory || workHistory.length === 0) return null;
    
    return (
        <Card className="bg-card border-border" data-testid="work-history">
            <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                    <HardHat className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-foreground">Work History</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                    {workHistory.map((item, idx) => (
                        <div
                            key={idx}
                            className="flex items-center gap-2 px-3 py-1.5 bg-accent/50 rounded-full text-sm"
                        >
                            <Wrench className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="text-muted-foreground">
                                {item.count} {item.trade}{item.count !== 1 ? 's' : ''}
                            </span>
                        </div>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                    Trade categories of contractors who have reviewed this property
                </p>
            </CardContent>
        </Card>
    );
};

/**
 * ANONYMIZED Review Card with Contractor Credibility Header
 * 
 * Shows contractor credibility WITHOUT exposing identity:
 * - Trade (e.g., Electrician)
 * - Tier/Rank level
 * - Verified Jobs count
 * - Reputation Score (if available)
 * 
 * NEVER shows: Contractor names, Company names
 */
const ReviewCard = ({ review }) => {
    const stars = review.overall_rating || 3;
    
    // Get tier number for display
    const getTierNumber = (tier) => {
        const tiers = { basic: 1, verified: 2, trusted: 3 };
        return tiers[tier] || 1;
    };
    
    return (
        <div className="p-4 bg-accent/30 rounded-lg border border-border" data-testid="review-card">
            {/* CONTRACTOR CREDIBILITY HEADER - Uber/Airbnb style */}
            <div className="flex items-start justify-between mb-3 pb-3 border-b border-border/50">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-foreground">
                            {review.contractor_trade || 'Contractor'}
                        </span>
                        <TierBadge tier={review.contractor_tier} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3 text-primary" />
                            Tier {getTierNumber(review.contractor_tier)} Contractor
                        </span>
                        <span>•</span>
                        <span>{review.contractor_review_count || 0} Verified Jobs</span>
                        {review.contractor_reputation_score && (
                            <>
                                <span>•</span>
                                <span className="text-primary font-medium">
                                    Rep: {review.contractor_reputation_score}
                                </span>
                            </>
                        )}
                    </div>
                    {review.contractor_rank && (
                        <div className="mt-1 text-xs text-muted-foreground/70">
                            Rank: {review.contractor_rank}
                        </div>
                    )}
                </div>
                {/* Star Rating with opinion label */}
                <div className="text-right">
                    <p className="text-[11px] text-muted-foreground/70 mb-1">Contractor's personal opinion</p>
                    <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                            <Star 
                                key={i} 
                                className={cn(
                                    "h-4 w-4",
                                    i < stars ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"
                                )} 
                            />
                        ))}
                    </div>
                </div>
            </div>
            
            {/* Job Type + Project Size */}
            <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-foreground">{review.job_type}</span>
                <span className="text-xs text-muted-foreground">• {review.project_size}</span>
                {review.has_verified_evidence && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
                        <ShieldCheck className="h-3 w-3" />
                        Verified
                    </span>
                )}
            </div>
            
            {/* Review Notes */}
            {review.notes && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">"{review.notes}"</p>
            )}

            {/* Timestamp */}
            <div className="text-xs text-muted-foreground text-right">
                {formatTimeAgo(review.created_at)}
            </div>
        </div>
    );
};

const PropertyDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [existingReview, setExistingReview] = useState(null);
    const [checkingExisting, setCheckingExisting] = useState(true);

    useEffect(() => {
        loadPropertyData();
        checkExistingReview();
    }, [id]);

    const loadPropertyData = async () => {
        try {
            const response = await api.get(`/properties/${id}/summary`);
            setData(response.data);
        } catch (err) {
            console.error('Failed to load property data:', err);
            toast.error('Failed to load property data');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const checkExistingReview = async () => {
        try {
            const response = await reviewAPI.getExisting(id);
            if (response.data.has_existing_review) {
                setExistingReview(response.data);
            }
        } catch (err) {
            // Ignore - user may not be logged in
        } finally {
            setCheckingExisting(false);
        }
    };

    // Confidence level styling
    const getConfidenceStyle = (level) => {
        const styles = {
            high: 'text-emerald-500',
            medium: 'text-amber-500',
            low: 'text-gray-400',
            insufficient: 'text-gray-400',
        };
        return styles[level] || styles.low;
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </Layout>
        );
    }

    if (!data) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center min-h-[60vh]">
                    <p className="text-muted-foreground">Property not found</p>
                    <Button variant="outline" onClick={() => navigate('/dashboard')} className="mt-4">
                        Back to Dashboard
                    </Button>
                </div>
            </Layout>
        );
    }

    const { property, scores, breakdown, quick_stats, recent_reviews, ownership_reset, protections, positive_indicators, work_history } = data;
    const reliabilityScore = scores?.reliability_score;
    const scoreVisible = scores?.score_visible === true;
    const minimumReviewsNeeded = scores?.minimum_reviews_needed || 0;
    const hasScore = scoreVisible && reliabilityScore !== null && reliabilityScore !== undefined;

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 py-4 sm:py-6">
                {/* Back Button */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(-1)}
                    className="mb-4 -ml-2"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                </Button>

                {/* Property Header */}
                <div className="mb-6">
                    <div className="flex items-start gap-3 mb-4">
                        <div className="p-2.5 rounded-lg bg-primary/10 flex-shrink-0">
                            <MapPin className="h-6 w-6 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h1 className="font-bold text-xl sm:text-2xl text-foreground" data-testid="property-address">
                                {property?.display_address}
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                {property?.city}{property?.state && `, ${property?.state}`} {property?.zip_code}
                            </p>
                        </div>
                    </div>

                    {/* Ownership Reset Notice */}
                    {ownership_reset?.has_reset && (
                        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-amber-500 font-medium">Property Ownership Changed</p>
                                    <p className="text-xs text-amber-500/80">
                                        Reset {formatDate(ownership_reset.reset_date)} • {ownership_reset.archived_count} archived reviews
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* PRIMARY CTA - Top of Page */}
                <div className="mb-6">
                    {!checkingExisting && (
                        existingReview?.has_existing_review && existingReview?.can_update ? (
                            <Link to={`/review/update/${existingReview.existing_review.id}`}>
                                <Button 
                                    className="w-full h-14 text-lg font-semibold gap-3" 
                                    variant="outline"
                                    data-testid="top-cta-update"
                                >
                                    <Edit className="h-5 w-5" />
                                    Update Your Review
                                </Button>
                            </Link>
                        ) : (
                            <Link to={`/review/new/${id}`}>
                                <Button 
                                    className="w-full h-14 text-lg font-semibold gap-3" 
                                    data-testid="top-cta-review"
                                >
                                    <Plus className="h-5 w-5" />
                                    Review Property Now
                                </Button>
                            </Link>
                        )
                    )}
                    {existingReview?.has_existing_review && (
                        <p className="text-xs text-center text-muted-foreground mt-2">
                            You reviewed this property on {formatDate(existingReview.existing_review.created_at)}
                        </p>
                    )}
                </div>

                {/* 1. CONTRACTOR JOB HISTORY - Hero Section */}
                <Card className="bg-card border-border mb-4 overflow-hidden">
                    <CardContent className="p-0">
                        {/* Header: Contractor Job History */}
                        <div className="p-4 border-b border-border bg-accent/30">
                            <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                                <Clipboard className="h-5 w-5 text-primary" />
                                Contractor Job History
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {quick_stats?.total_reviews || 0} contractor-reported job experience{(quick_stats?.total_reviews || 0) !== 1 ? 's' : ''} at this address
                            </p>
                        </div>
                        
                        <div className="p-6 text-center">
                            {hasScore ? (
                                <>
                                    {/* Contractor Experience Summary */}
                                    <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
                                        Contractor Experience Summary
                                    </h3>
                                    
                                    {/* Score Display - X/5 format */}
                                    <ExperienceScoreDisplay 
                                        score={reliabilityScore} 
                                        reviewCount={quick_stats?.total_reviews || 0}
                                        size="default"
                                    />
                                    
                                    {/* Score Summary */}
                                    <p className="text-sm text-muted-foreground max-w-sm mx-auto mt-4">
                                        {scores?.score_summary}
                                    </p>
                                </>
                            ) : (
                                <>
                                    {/* NO DATA YET - Enhanced Empty State */}
                                    <div className="py-4">
                                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <FileText className="h-8 w-8 text-primary" />
                                        </div>
                                        <h3 className="text-lg font-bold text-foreground mb-2">
                                            No contractor job history yet
                                        </h3>
                                        <p className="text-muted-foreground text-sm mb-6 max-w-xs mx-auto">
                                            Be the first contractor to log this job and help others know what to expect.
                                        </p>
                                        <Link to={`/review/new/${id}`}>
                                            <Button size="lg" className="h-14 px-8 text-base" data-testid="add-first-review-btn">
                                                <Plus className="h-5 w-5 mr-2" />
                                                Review Property Now
                                            </Button>
                                        </Link>
                                    </div>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* QUICK STATS SUMMARY - Always visible */}
                <Card className="bg-card border-border mb-4">
                    <CardContent className="p-4">
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Users className="h-4 w-4 text-primary" />
                            Contractor Reports
                        </h3>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="text-center p-3 bg-accent/30 rounded-lg">
                                <p className="text-2xl font-bold text-foreground">{quick_stats?.total_reviews || 0}</p>
                                <p className="text-xs text-muted-foreground">Reviews</p>
                            </div>
                            <div className="text-center p-3 bg-accent/30 rounded-lg">
                                <p className="text-2xl font-bold text-foreground">
                                    {hasScore ? quick_stats?.payment_rate : '--'}
                                </p>
                                <p className="text-xs text-muted-foreground">Paid</p>
                            </div>
                            <div className="text-center p-3 bg-accent/30 rounded-lg">
                                <p className="text-2xl font-bold text-foreground">
                                    {hasScore ? quick_stats?.work_again_rate : '--'}
                                </p>
                                <p className="text-xs text-muted-foreground">Would Return</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* TRADES THAT WORKED HERE - Always visible */}
                <Card className="bg-card border-border mb-4" data-testid="work-history-section">
                    <CardContent className="p-4">
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                            <HardHat className="h-4 w-4 text-primary" />
                            Trades That Worked Here
                        </h3>
                        {work_history && work_history.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {work_history.map((item, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center gap-2 px-3 py-2 bg-accent/50 rounded-lg"
                                    >
                                        <Wrench className="h-4 w-4 text-muted-foreground" />
                                        <span className="text-sm font-medium text-foreground">
                                            {item.count} {item.trade}{item.count !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No trade data yet. Be the first to log your work here.
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* CLIENT SIGNALS - Always visible */}
                <Card className={cn(
                    "border mb-4",
                    positive_indicators && positive_indicators.length > 0 
                        ? "bg-emerald-500/5 border-emerald-500/20" 
                        : "bg-card border-border"
                )} data-testid="client-signals-section">
                    <CardContent className="p-4">
                        <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Award className="h-4 w-4 text-emerald-500" />
                            Client Signals
                        </h3>
                        {positive_indicators && positive_indicators.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {positive_indicators.map((indicator) => {
                                    const iconMap = {
                                        'dollar-sign': DollarSign,
                                        'thumbs-up': ThumbsUp,
                                        'clipboard-check': ClipboardCheck,
                                        'zap': Zap,
                                        'award': Award
                                    };
                                    const Icon = iconMap[indicator.icon] || CheckCircle;
                                    return (
                                        <div
                                            key={indicator.id}
                                            className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30"
                                            title={indicator.description}
                                        >
                                            <Icon className="h-4 w-4 text-emerald-500" />
                                            <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                                                {indicator.name}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No client signals yet. Signals appear after 3+ positive reviews.
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* 2. CONTRACTOR SENTIMENT */}
                {hasScore && (
                    <div className="mb-4">
                        <ContractorSentiment 
                            percentage={quick_stats?.work_again_rate_raw !== null ? quick_stats?.work_again_rate_raw * 100 : null}
                            totalReviews={quick_stats?.total_reviews}
                        />
                    </div>
                )}

                {/* 3. RECOMMENDED PROTECTIONS */}
                {protections && (
                    <div className="mb-4">
                        <ProtectionRecommendations protections={protections} />
                    </div>
                )}

                {/* 4. SCORE BREAKDOWN */}
                {hasScore && breakdown && (
                    <Card className="bg-card border-border mb-4">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-4">
                                <TrendingUp className="h-4 w-4 text-primary" />
                                <h3 className="font-semibold text-foreground">Score Breakdown</h3>
                            </div>
                            
                            <div className="space-y-4">
                                <ScoreBar 
                                    label="My payment experience" 
                                    score={breakdown?.payment_reliability?.score}
                                    scoreLabel={breakdown?.payment_reliability?.label}
                                    icon={DollarSign}
                                />
                                <ScoreBar 
                                    label="My communication experience" 
                                    score={breakdown?.communication?.score}
                                    scoreLabel={breakdown?.communication?.label}
                                    icon={MessageSquare}
                                />
                                <ScoreBar 
                                    label="My scope change experience" 
                                    score={breakdown?.scope_change_risk?.score}
                                    scoreLabel={breakdown?.scope_change_risk?.label}
                                    icon={RefreshCw}
                                />
                                <ScoreBar 
                                    label="My contract adherence experience" 
                                    score={breakdown?.contract_adherence?.score}
                                    scoreLabel={breakdown?.contract_adherence?.label}
                                    icon={Handshake}
                                />
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Quick Stats Row */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <QuickStat 
                        icon={FileText}
                        value={quick_stats?.total_reviews || 0}
                        label="Reviews"
                        color="primary"
                    />
                    <QuickStat 
                        icon={DollarSign}
                        value={hasScore ? (quick_stats?.payment_rate || 'N/A') : '—'}
                        label="Paid"
                        color={hasScore && quick_stats?.payment_rate_raw >= 0.9 ? 'emerald' : hasScore && quick_stats?.payment_rate_raw >= 0.7 ? 'amber' : 'primary'}
                    />
                    <QuickStat 
                        icon={Users}
                        value={hasScore ? (quick_stats?.work_again_rate || 'N/A') : '—'}
                        label="Would Return"
                        color={hasScore && quick_stats?.work_again_rate_raw >= 0.9 ? 'emerald' : hasScore && quick_stats?.work_again_rate_raw >= 0.7 ? 'amber' : 'primary'}
                    />
                </div>

                {/* 5. CONTRACTOR REVIEWS */}
                <Card className="bg-card border-border">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-primary" />
                                <h3 className="font-medium text-foreground">Recent Contractor Experiences</h3>
                            </div>
                        </div>

                        {recent_reviews?.length > 0 ? (
                            <div className="space-y-3">
                                {recent_reviews.map((review, idx) => (
                                    <ReviewCard key={review.id || idx} review={review} />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground mb-3">No reviews yet</p>
                                <Link to={`/review/new/${id}`}>
                                    <Button variant="outline" size="sm">
                                        <Plus className="h-4 w-4 mr-1" />
                                        Review Property Now
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* How This Score Is Calculated - Transparency Section */}
                {hasScore && (
                    <Card className="mt-4 bg-card border-border">
                        <CardContent className="p-4">
                            <details className="group">
                                <summary className="flex items-center justify-between cursor-pointer list-none">
                                    <h3 className="font-medium text-foreground flex items-center gap-2">
                                        <Scale className="h-4 w-4 text-primary" />
                                        How this score is calculated
                                    </h3>
                                    <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
                                </summary>
                                <div className="mt-4 space-y-3 text-sm text-muted-foreground">
                                    <p>
                                        ClientCheck summarizes contractor-submitted job experience ratings for this address 
                                        using a simple average of review inputs.
                                    </p>
                                    <div>
                                        <p className="font-medium text-foreground mb-2">Ratings consider:</p>
                                        <ul className="list-disc list-inside space-y-1 ml-2">
                                            <li>Payment timeliness</li>
                                            <li>Communication quality</li>
                                            <li>Scope changes</li>
                                            <li>Site readiness</li>
                                            <li>Overall experience</li>
                                        </ul>
                                    </div>
                                    <p className="pt-2 border-t border-border">
                                        <strong>Note:</strong> ClientCheck does not independently verify job outcomes. 
                                        The score is a mathematical average of contractor-submitted ratings with no hidden weighting.
                                    </p>
                                </div>
                            </details>
                        </CardContent>
                    </Card>
                )}

                {/* User-Generated Content Disclaimer */}
                <div className="mt-6 p-4 bg-muted/30 border border-border rounded-lg">
                    <p className="text-xs text-muted-foreground text-center leading-relaxed">
                        Contractor reviews reflect individual job experiences submitted by independent contractors. 
                        ClientCheck aggregates contractor feedback but does not independently verify the accuracy of submitted reviews.
                    </p>
                </div>
            </div>
        </Layout>
    );
};

export default PropertyDetails;
